import { useState, useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';

// -----------------------------------------------------------------------------
export interface InternalUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  specialization: string;
  role: string;
  accessPolicy: string;
}

// -----------------------------------------------------------------------------
interface UseCreateInternalUserReturn {
  createUser: (userData: InternalUserFormData) => Promise<boolean>;
  loading: boolean;
}

// -----------------------------------------------------------------------------
export const useCreateInternalUser = (
  onUserCreated?: () => void
): UseCreateInternalUserReturn => {
  const medplum = useMedplum();
  const { t } = useTranslation(['admin', 'common']);
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  const createUser = useCallback(
    async (values: InternalUserFormData): Promise<boolean> => {
      setLoading(true);
      try {
        const currentProject = medplum.getActiveLogin()?.project;
        if (!currentProject?.reference) {
          throw new Error('No active project found');
        } // Send invitation using Medplum's admin invite endpoint
        // This will create the Practitioner resource and send an invitation email
        await medplum.post(
          'admin/projects/' +
            currentProject.reference?.split('/')[1] +
            '/invite',
          {
            resourceType: 'Practitioner',
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            sendEmail: true,
            membership: {
              // Set admin flag based on role
              ...(values.role === 'admin' && { admin: true }),
              // If access policy is provided, use it
              ...(values.accessPolicy && {
                accessPolicy: {
                  reference: `AccessPolicy/${values.accessPolicy}`,
                },
              }),
            },
          }
        );

        notifications.show({
          color: 'green',
          title: t('success', { ns: 'common' }),
          message: t('userManagement.notifications.userInvited', {
            ns: 'admin',
            defaultValue:
              'User has been invited successfully. They will receive an invitation email.',
          }),
        });

        // Refresh the parent component
        onUserCreated?.();

        return true;
      } catch (error) {
        console.error('Error inviting internal user:', error);
        notifications.show({
          color: 'red',
          title: t('error', { ns: 'common' }),
          message: t('userManagement.notifications.inviteError', {
            ns: 'admin',
            defaultValue: 'Failed to invite user. Please try again.',
          }),
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [medplum, t, onUserCreated]
  );

  // ---------------------------------------------------------------------------
  return {
    createUser,
    loading,
  };
};
